import { useState } from "react";
import { Box, Button, Group, Paper, Stack, Text } from "@mantine/core";
import CountryFlag from "@/components/shared/CountryFlag";
import CorridorDoubleArrow from "@/components/shared/CorridorDoubleArrow";
import {
  ECOSYSTEM_CORRIDOR_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import type { ComingSoonEcosystemCorridor } from "@/shared/constants/comingSoonEcosystemCorridors.const";
import {
  comingSoonCorridorHasFlags,
  formatComingSoonCorridorPair,
} from "@/shared/constants/comingSoonEcosystemCorridors.const";
import classes from "./EcosystemCorridorPlaceholderCard.module.css";

const CARD_BORDER = "#BFDBFE";
const CARD_BLUE = "#2563EB";
const FLAG_BORDER = "#BFDBFE";

type EcosystemCorridorPlaceholderCardProps = {
  corridor: ComingSoonEcosystemCorridor;
  /** Fixed width for horizontal scroll layouts (e.g. explore page). */
  fixedWidth?: number;
  fullWidth?: boolean;
  /** Use compact flag badges (sector carousel) vs inline flags (explore grid). */
  variant?: "carousel" | "explore";
};

function FlagBadge({ country }: { country: string }) {
  return (
    <Box
      style={{
        padding: 8,
        borderRadius: 12,
        backgroundColor: "#FFFFFF",
        border: `1px solid ${FLAG_BORDER}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box style={{ transform: "scale(1.4)", transformOrigin: "center", lineHeight: 0 }}>
        <CountryFlag country={country} />
      </Box>
    </Box>
  );
}

export default function EcosystemCorridorPlaceholderCard({
  corridor,
  fixedWidth,
  fullWidth = false,
  variant = "carousel",
}: EcosystemCorridorPlaceholderCardProps) {
  const isUnderway = corridor.status === "underway";
  const buttonLabel = isUnderway ? "Underway" : "Coming Soon";
  const showFlags = comingSoonCorridorHasFlags(corridor);
  const corridorPair = formatComingSoonCorridorPair(corridor);
  const isExplore = variant === "explore";
  const cardHeight = isExplore ? ECOSYSTEM_EXPLORE_CARD_HEIGHT : ECOSYSTEM_CORRIDOR_CARD_HEIGHT;
  const cardWidth = fullWidth && isExplore ? undefined : isExplore ? ECOSYSTEM_EXPLORE_CARD_WIDTH : fixedWidth;
  const [isHovered, setIsHovered] = useState(false);

  const flagsRow = showFlags ? (
    <Group gap="sm" justify="center" wrap="nowrap">
      <FlagBadge country={corridor.country!} />
      <CorridorDoubleArrow color={CARD_BLUE} />
      <FlagBadge country={corridor.targetCountry!} />
    </Group>
  ) : null;

  return (
    <Paper
      radius="lg"
      shadow="none"
      h={cardHeight}
      onMouseEnter={isExplore ? () => setIsHovered(true) : undefined}
      onMouseLeave={isExplore ? () => setIsHovered(false) : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${isExplore && isHovered ? "#93C5FD" : CARD_BORDER}`,
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        ...(cardWidth
          ? { flex: "0 0 auto", width: cardWidth, maxWidth: "85vw" }
          : fullWidth && isExplore
            ? { width: "100%" }
            : undefined),
        ...(isExplore
          ? {
              boxShadow: isHovered
                ? "0 16px 40px rgba(4, 48, 92, 0.18)"
                : "0 4px 20px rgba(15, 23, 42, 0.08)",
              transition: "box-shadow 0.25s ease, border-color 0.25s ease",
            }
          : undefined),
      }}
    >
      <Stack gap="md" p="lg" style={{ flex: 1, minHeight: 0 }}>
        <Stack gap="sm" align="center" style={{ flex: 1 }}>
          {flagsRow}

          <Stack gap={4} align="center">
            <Text
              fw={700}
              size="sm"
              tt="uppercase"
              ta="center"
              c="#04305c"
              lineClamp={2}
              lh={1.35}
            >
              {corridorPair}
            </Text>
            <Text
              fw={600}
              size="xs"
              tt="uppercase"
              ta="center"
              c="#04305c"
              lineClamp={2}
              lh={1.35}
              style={{ opacity: 0.88 }}
            >
              {corridor.corridorSubtitle}
            </Text>
          </Stack>
        </Stack>

        <Button
          size="sm"
          fullWidth
          disabled
          mt="auto"
          classNames={{ root: classes.footerButton }}
        >
          {buttonLabel}
        </Button>
      </Stack>
    </Paper>
  );
}
